import React, { useState } from 'react'
import { Button, Flex, Modal, Text, TextVariant } from '@mprolab/uikit'
import { useTranslation } from 'react-i18next'
import styled, { useTheme } from 'styled-components'
import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import { isLastItem } from '@mprolab/sdk'
import { displayDuration } from 'utils/displayDuration'
import { useStakeConfig } from '../autoStake.logic'
import { AutostakeProgress, AutostakeRowInfo } from './components'
import DeclarationCounter from '../components/DeclarationCounter'

dayjs.extend(duration)

const TableWrapper = styled(Flex)`
  padding: 16px;
  border-radius: 8px;
  justify-content: center;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.gray10};
`

const Divider = styled(Flex)`
  width: 100%;
  height: 1px;
  border-top: 1px solid ${({ theme }) => theme.colors.gray6};
  opacity: 0.3;
  margin: 8px 0;
`

const CounterWrapper = styled(Flex)`
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.gray10};
  padding: 8px 8px 16px 8px;
  min-height: 70px;
`

enum AutostakeViewProgress {
  WARNING,
  INITIAL
}

export interface JoinAutostakeProps {
  timeUntilEnd: number
  counterLoading: boolean
  progress: number
  onDismiss?: () => void
  availableStakeAddress?: string
}

const JoinAutoStake: React.FC<JoinAutostakeProps> = ({
  onDismiss,
  counterLoading,
  progress,
  timeUntilEnd,
  availableStakeAddress
}) => {
  const WARNING_MESSAGE_TIME = 86400000 // 24 hours
  const { t } = useTranslation()
  const { colors } = useTheme()
  const { config, isLoading } = useStakeConfig(availableStakeAddress)
  const displayWarning = timeUntilEnd < WARNING_MESSAGE_TIME
  const [contentProgress, setContentProgress] = useState(
    displayWarning ? AutostakeViewProgress.WARNING : AutostakeViewProgress.INITIAL
  )

  const modalTitle = () => {
    switch (contentProgress) {
      case AutostakeViewProgress.WARNING:
        return t('autoStake.warning')
      case AutostakeViewProgress.INITIAL:
        return t('autoStake.title')
      default:
        return ''
    }
  }

  const modalContent = () => {
    switch (contentProgress) {
      case AutostakeViewProgress.WARNING:
        return (
          <Flex flexDirection="column" gap="24px" mt="16px">
            <Flex flexDirection="column" gap="16px">
              <Text color={colors.gray6}>{t('autoStake.warningDescription')}</Text>
            </Flex>
            <CounterWrapper>
              {!counterLoading && (
                <DeclarationCounter
                  title={t('autoStake.autostakeEndsIn')}
                  {...{ timeUntilEnd, progress, counterLoading }}
                />
              )}
            </CounterWrapper>
            <Button fullWidth onClick={() => setContentProgress(AutostakeViewProgress.INITIAL)}>
              {t('autoStake.joinAutoStake')}
            </Button>
          </Flex>
        )
      case AutostakeViewProgress.INITIAL:
        return (
          <>
            <Flex flexDirection="column" gap="16px" mt="16px">
              <Text color={colors.gray6}>{t('autoStake.description')}</Text>
            </Flex>
            <Flex mt="32px" mb="8px">
              <Text variant={TextVariant.BODY_SMALL} textTransform="uppercase" color={colors.gray6}>
                {t('autoStake.overview')}
              </Text>
            </Flex>
            <TableWrapper>
              {Object.entries(config).map(([description, value], index) => {
                const isLast = isLastItem(Object.entries(config), index)

                let valueColor = colors.white
                let labelColor = colors.gray6
                let valueType = ''
                let formattedValue = value
                let boldLabel = false

                switch (description) {
                  case 'totalStakeDuration':
                  case 'remainingStakeDuration':
                    formattedValue = displayDuration({ time: value })
                    break
                  case 'apy':
                    valueColor = colors.green
                    valueType = '%'
                    break
                  case 'estimatedReward':
                    valueColor = colors.green
                    valueType = 'MPRO'
                    labelColor = colors.white
                    boldLabel = true
                    break
                  case 'initialStakeAmount':
                    valueType = 'MPRO'
                    break
                }

                return (
                  <React.Fragment key={description}>
                    <AutostakeRowInfo
                      label={t(`autoStake.${description}`)}
                      {...{
                        valueColor,
                        labelColor,
                        value: formattedValue,
                        valueType,
                        boldLabel,
                        isLoading
                      }}
                    />
                    {!isLast && <Divider />}
                  </React.Fragment>
                )
              })}
            </TableWrapper>
            <Flex justifyContent="center" mt="32px">
              <AutostakeProgress {...{ availableStakeAddress }} />
            </Flex>
          </>
        )
      default:
        return <></>
    }
  }

  return (
    <Modal style={{ padding: '32px' }} {...{ onDismiss }} title={modalTitle()}>
      <Flex flexDirection="column" maxWidth={502}>
        {modalContent()}
      </Flex>
    </Modal>
  )
}

export default JoinAutoStake
